import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  // Bumped every time a live notification arrives — pages observing this value
  // can refetch their notification list to stay in sync.
  const [notificationVersion, setNotificationVersion] = useState(0);
  const wsRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectTimerRef = useRef(null);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  // Realtime notifications channel — opens whenever we have a valid token.
  useEffect(() => {
    if (!token) {
      closeSocket();
      return;
    }
    openSocket(token);
    return () => closeSocket();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openSocket = (jwt) => {
    closeSocket();
    try {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${wsProtocol}//${window.location.host}/api/ws/notifications?token=${encodeURIComponent(jwt)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        // Keep the connection alive through proxies that drop idle sockets.
        pingTimerRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send('ping');
        }, 25000);
      };

      ws.onmessage = (evt) => {
        // Server sends JSON for events; plain text for pongs.
        try {
          const data = JSON.parse(evt.data);
          if (data?.event === 'notification' && data?.data) {
            toast(data.data.title || 'Nouvelle notification', {
              description: data.data.message || '',
            });
            setNotificationVersion((v) => v + 1);
          }
        } catch (_e) {
          // Non-JSON (e.g. "pong") — ignore.
        }
      };

      ws.onclose = () => {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
        // Light auto-reconnect if we still have a token
        if (token) {
          reconnectTimerRef.current = setTimeout(() => openSocket(token), 5000);
        }
      };

      ws.onerror = () => {
        // ws.onclose will handle reconnect; nothing else to do here.
      };
    } catch (e) {
      console.error('WS connect failed', e);
    }
  };

  const closeSocket = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
    if (wsRef.current) {
      try {
        // Prevent reconnect during intentional close
        wsRef.current.onclose = null;
        wsRef.current.close();
      } catch (_e) { /* ignore */ }
      wsRef.current = null;
    }
  };

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    setToken(response.data.token);
    setUser(response.data.user);
    localStorage.setItem('token', response.data.token);
    return response.data;
  };

  const setGoogleAuth = (tokenValue, userData) => {
    localStorage.setItem('token', tokenValue);
    setToken(tokenValue);
    setUser(userData);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, setGoogleAuth, loading, notificationVersion }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
